import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  Plus, Search, FileText, Clock, CheckCircle, XCircle, AlertCircle,
  Send, Calendar, Trash2,
  UserPlus, Phone, MessageSquare, Mail, X, Check, ChevronRight,
  RefreshCw, ExternalLink, MapPin, CheckSquare, Square, Eye, AlertTriangle
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  formatFullName, formatDate, formatCurrency,
  isCustomEstimate, resolveEstimatePrice, resolveClientName, resolveAddress,
  getLeadStatus, getServiceLabel, buildGoogleCalendarUrl
} from '../utils/helpers';
import { estimateStatuses, leadStatuses, leadStatusFlow, closeLostReasons, cleaningFrequencies, homeConditions, ESTIMATE_VALIDITY_DAYS } from '../utils/estimateData';
import { sendWalkthroughInvite, sendLeadFollowUpEmail } from '../utils/emailApi';
import './Estimates.css';
import './Leads.css';

export default function Estimates() {
  const navigate = useNavigate();
  const { state, updateEstimate, getClientById, getPropertyById, deleteEstimate, addTodo, addCommunicationLog } = useApp();
  const { estimates = [] } = state;

  // View mode: pipeline (default) or list
  const [view, setView] = useState('pipeline');

  // Shared
  const [searchTerm, setSearchTerm] = useState('');

  // Pipeline state
  const [pipelineFilter, setPipelineFilter] = useState('new');
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [showCloseLost, setShowCloseLost] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [lostReasonNote, setLostReasonNote] = useState('');

  // Walkthrough scheduling modal
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughData, setWalkthroughData] = useState({ date: '', time: '10:00', duration: 30, notes: '', sendToClient: true });
  const [walkthroughSending, setWalkthroughSending] = useState(false);

  // Follow-up email modal state
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpSelected, setFollowUpSelected] = useState({});
  const [followUpSending, setFollowUpSending] = useState(false);
  const [followUpResults, setFollowUpResults] = useState(null);
  const [followUpPreviewLead, setFollowUpPreviewLead] = useState(null);

  // List state
  const [listStatusFilter, setListStatusFilter] = useState('all');

  // ===== ESTIMATE EXPIRATION — run once on mount =====
  useEffect(() => {
    const now = new Date();
    estimates.forEach(est => {
      if (est.status === 'sent' && est.validUntil && new Date(est.validUntil) < now) {
        updateEstimate(est.id, { status: 'expired' }).catch(console.error);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run only on mount

  // ===== Enrich estimates once =====
  const enriched = useMemo(() => {
    return estimates.map(est => {
      const client = getClientById(est.clientId);
      const property = getPropertyById(est.propertyId);
      return {
        ...est,
        _ls: getLeadStatus(est),
        _name: resolveClientName(est, client),
        _client: client,
        _property: property,
        _addr: resolveAddress(est, property),
        _svc: getServiceLabel(est),
        _amt: resolveEstimatePrice(est),
        _custom: isCustomEstimate(est),
        _at: est.createdAt || est.submittedAt || est.created,
        _src: est.source || (est.requiresReview ? 'website' : 'manual'),
        _act: est.leadActivity || [],
      };
    });
  }, [estimates, getClientById, getPropertyById]);

  // ===== PIPELINE =====
  const pipelineLeads = useMemo(() => {
    return enriched
      .filter(l => {
        if (pipelineFilter !== 'all' && l._ls !== pipelineFilter) return false;
        if (searchTerm) {
          const s = searchTerm.toLowerCase();
          return l._name.toLowerCase().includes(s) || l._addr.toLowerCase().includes(s) ||
            (l.clientEmail || l._client?.email || '').toLowerCase().includes(s);
        }
        return true;
      })
      .sort((a, b) => {
        if (a._ls === 'new' && b._ls !== 'new') return -1;
        if (b._ls === 'new' && a._ls !== 'new') return 1;
        return new Date(b._at || 0) - new Date(a._at || 0);
      });
  }, [enriched, pipelineFilter, searchTerm]);

  const plCounts = useMemo(() => {
    const c = { new: 0, contacted: 0, walkthrough_scheduled: 0, estimate_in_progress: 0, estimate_sent: 0, follow_up: 0, won: 0, lost: 0, all: 0 };
    enriched.forEach(e => { c[e._ls] = (c[e._ls] || 0) + 1; c.all++; });
    return c;
  }, [enriched]);

  // ===== LIST =====
  const filteredList = useMemo(() => {
    return enriched
      .filter(est => {
        if (listStatusFilter !== 'all' && est.status !== listStatusFilter) return false;
        if (searchTerm) {
          const s = searchTerm.toLowerCase();
          return est._name.toLowerCase().includes(s) || est._addr.toLowerCase().includes(s);
        }
        return true;
      })
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [enriched, listStatusFilter, searchTerm]);

  const listCounts = useMemo(() => {
    const c = { all: estimates.length };
    estimates.forEach(e => { c[e.status] = (c[e.status] || 0) + 1; });
    return c;
  }, [estimates]);

  // ===== PIPELINE ACTIONS =====
  const updateLeadStatus = useCallback(async (leadId, newStatus, extra = {}) => {
    // Intercept: walkthrough_scheduled triggers the walkthrough modal
    if (newStatus === 'walkthrough_scheduled' && !extra._walkthroughConfirmed) {
      setShowWalkthrough(true);
      return;
    }

    const now = new Date().toISOString();
    const lead = estimates.find(e => e.id === leadId);
    const cur = lead?.leadActivity || [];
    const entry = {
      type: 'status',
      text: `Status → ${leadStatuses[newStatus]?.label || newStatus}`,
      timestamp: now, user: 'Sarah',
      ...extra.activityExtra,
    };
    const updates = {
      leadStatus: newStatus,
      leadActivity: [...cur, entry],
      leadStatusUpdatedAt: now,
      ...extra.updates,
    };
    if (newStatus === 'won') updates.status = 'approved';
    if (newStatus === 'lost') updates.status = 'declined';
    if (newStatus === 'estimate_sent' && lead?.status === 'pending_review') updates.status = 'sent';

    await updateEstimate(leadId, updates);

    // Auto-todo on follow_up
    if (newStatus === 'follow_up' && addTodo) {
      const clientName = resolveClientName(lead, getClientById(lead?.clientId));
      try {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 3);
        await addTodo({
          text: `Follow up with ${clientName} re: estimate`,
          priority: 'high',
          category: 'sales',
          dueDate: dueDate.toISOString().split('T')[0],
          assignedTo: 'admin',
          relatedId: leadId,
          relatedType: 'estimate',
        });
        // Save the todo reference back on the estimate
        await updateEstimate(leadId, { followUpTodoCreated: now });
      } catch (err) { console.error('Failed to create follow-up todo:', err); }
    }

    if (selectedLead?.id === leadId) {
      setSelectedLead(prev => prev ? { ...prev, ...updates, _ls: newStatus, _act: updates.leadActivity } : null);
    }
  }, [estimates, updateEstimate, selectedLead, addTodo, getClientById]);

  // ===== WALKTHROUGH CONFIRM =====
  const handleWalkthroughConfirm = useCallback(async () => {
    if (!selectedLead || !walkthroughData.date) return;
    setWalkthroughSending(true);

    try {
      const lead = selectedLead;
      const clientName = lead._name;
      const address = lead._addr;
      const clientEmail = lead.clientEmail || lead._client?.email || '';

      // Build Google Calendar URL
      const calendarUrl = buildGoogleCalendarUrl({
        title: `Walkthrough: ${clientName}`,
        startDate: walkthroughData.date,
        startTime: walkthroughData.time || '10:00',
        durationMinutes: walkthroughData.duration || 30,
        location: address,
        description: `Estimate walkthrough for ${clientName}\n${walkthroughData.notes || ''}`,
      });

      // Send email with calendar link
      try {
        await sendWalkthroughInvite({
          adminEmail: 'hello@twosisterscleaningco.com',
          clientEmail: walkthroughData.sendToClient ? clientEmail : null,
          clientName,
          date: walkthroughData.date,
          time: walkthroughData.time,
          durationMinutes: walkthroughData.duration,
          address,
          notes: walkthroughData.notes,
          sendToClient: walkthroughData.sendToClient && !!clientEmail,
          calendarUrl,
        });
      } catch (emailErr) {
        console.error('Walkthrough email failed (continuing):', emailErr);
      }

      // Now update the status with walkthrough data
      await updateLeadStatus(lead.id, 'walkthrough_scheduled', {
        _walkthroughConfirmed: true,
        updates: {
          walkthroughDate: walkthroughData.date,
          walkthroughTime: walkthroughData.time,
          walkthroughNotes: walkthroughData.notes,
          walkthroughDuration: walkthroughData.duration,
        },
        activityExtra: {
          text: `Walkthrough scheduled for ${format(new Date(walkthroughData.date + 'T00:00:00'), 'MMM d, yyyy')} at ${walkthroughData.time || 'TBD'}`,
        },
      });

      // Create a todo for the walkthrough
      if (addTodo) {
        try {
          await addTodo({
            text: `Walkthrough: ${clientName} at ${address}`,
            priority: 'high',
            category: 'sales',
            dueDate: walkthroughData.date,
            assignedTo: 'admin',
            relatedId: lead.id,
            relatedType: 'estimate',
          });
        } catch (err) { console.error('Failed to create walkthrough todo:', err); }
      }

      setShowWalkthrough(false);
      setWalkthroughData({ date: '', time: '10:00', duration: 30, notes: '', sendToClient: true });
    } catch (err) {
      console.error('Walkthrough scheduling failed:', err);
      alert('Failed to schedule walkthrough. Please try again.');
    } finally {
      setWalkthroughSending(false);
    }
  }, [selectedLead, walkthroughData, updateLeadStatus, addTodo]);

  const addNote = useCallback(async () => {
    if (!newNote.trim() || !selectedLead) return;
    const entry = { type: 'note', text: newNote.trim(), timestamp: new Date().toISOString(), user: 'Sarah' };
    const updated = [...(selectedLead._act || []), entry];
    await updateEstimate(selectedLead.id, { leadActivity: updated });
    setSelectedLead(prev => prev ? { ...prev, _act: updated } : null);
    setNewNote('');
  }, [newNote, selectedLead, updateEstimate]);

  const handleCloseLost = useCallback(async () => {
    if (!lostReason || !selectedLead) return;
    const label = closeLostReasons.find(r => r.value === lostReason)?.label || lostReason;
    await updateLeadStatus(selectedLead.id, 'lost', {
      updates: { lostReason, lostReasonNote, lostAt: new Date().toISOString() },
      activityExtra: { text: `Closed Lost — ${label}${lostReasonNote ? `: ${lostReasonNote}` : ''}` },
    });
    setShowCloseLost(false); setLostReason(''); setLostReasonNote('');
  }, [selectedLead, lostReason, lostReasonNote, updateLeadStatus]);

  const timeAgo = (d) => { if (!d) return ''; try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ''; } };
  const goTo = (est) => navigate(`/admin/estimates/${est.id}`);

  // ===== FOLLOW-UP EMAIL LOGIC =====
  const followUpEligibleLeads = useMemo(() => {
    return enriched
      .filter(l => ['estimate_sent', 'follow_up'].includes(l._ls) && (l.clientEmail || l._client?.email))
      .map(l => ({
        ...l,
        _email: l.clientEmail || l._client?.email || '',
        _dateSent: l.leadStatusUpdatedAt || l.sentAt || l.updatedAt || l.createdAt,
        _approvalLink: l.approvalToken
          ? `https://keelgroup.app/portal/approve/${l.approvalToken}`
          : 'https://keelgroup.app/portal',
      }))
      .sort((a, b) => new Date(a._dateSent || 0) - new Date(b._dateSent || 0));
  }, [enriched]);

  const followUpToggle = useCallback((id) => {
    setFollowUpSelected(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const followUpSelectAll = useCallback(() => {
    const allSelected = followUpEligibleLeads.every(l => followUpSelected[l.id]);
    if (allSelected) {
      setFollowUpSelected({});
    } else {
      const sel = {};
      followUpEligibleLeads.forEach(l => { sel[l.id] = true; });
      setFollowUpSelected(sel);
    }
  }, [followUpEligibleLeads, followUpSelected]);

  const followUpSelectedCount = useMemo(() => {
    return followUpEligibleLeads.filter(l => followUpSelected[l.id]).length;
  }, [followUpEligibleLeads, followUpSelected]);

  const handleBatchFollowUp = useCallback(async () => {
    const selected = followUpEligibleLeads.filter(l => followUpSelected[l.id]);
    if (selected.length === 0) return;

    setFollowUpSending(true);
    setFollowUpResults(null);
    const results = { sent: 0, failed: 0, errors: [] };

    for (const lead of selected) {
      try {
        await sendLeadFollowUpEmail({
          clientEmail: lead._email,
          clientName: lead._name,
          estimateTotal: lead._amt,
          approvalLink: lead._approvalLink,
          propertyAddress: lead._addr,
          serviceLabel: lead._svc,
          dateSent: lead._dateSent,
        });

        const now = new Date().toISOString();
        const currentActivity = lead.leadActivity || lead._act || [];
        const activityEntry = { type: 'email', text: 'Follow-up email sent', timestamp: now, user: 'Sarah' };
        const updates = {
          leadActivity: [...currentActivity, activityEntry],
          leadStatusUpdatedAt: now,
        };
        if (lead._ls !== 'follow_up') {
          updates.leadStatus = 'follow_up';
        }
        await updateEstimate(lead.id, updates);

        await addCommunicationLog({
          clientId: lead.clientId,
          clientName: lead._name,
          communicationType: 'email',
          note: `Follow-up email sent regarding estimate ${lead.estimateNumber || lead.id?.slice(-6)}`,
          communicationDate: format(new Date(), 'yyyy-MM-dd'),
          communicationTime: format(new Date(), 'HH:mm'),
        });

        results.sent++;
      } catch (err) {
        results.failed++;
        results.errors.push(`${lead._name}: ${err.message}`);
      }
    }

    setFollowUpSending(false);
    setFollowUpResults(results);
    if (results.sent > 0) setFollowUpSelected({});
  }, [followUpEligibleLeads, followUpSelected, updateEstimate, addCommunicationLog]);

  const openFollowUpModal = useCallback(() => {
    setFollowUpSelected({});
    setFollowUpResults(null);
    setFollowUpPreviewLead(null);
    setShowFollowUpModal(true);
  }, []);

  return (
    <div className="estimates-page">
      {/* ===== HEADER ===== */}
      <div className="est-header">
        <div className="est-header-left">
          <h1>Estimates & Leads</h1>
          <p className="est-header-sub">
            {plCounts.all} total · {plCounts.new} new · {formatCurrency(
              enriched.filter(l => ['new', 'contacted', 'walkthrough_scheduled', 'estimate_in_progress', 'estimate_sent', 'follow_up'].includes(l._ls)).reduce((s, l) => s + l._amt, 0)
            )} pipeline
          </p>
        </div>
        <div className="est-header-right">
          <div className="view-toggle">
            <button className={`view-toggle-btn ${view === 'pipeline' ? 'active' : ''}`} onClick={() => setView('pipeline')}>
              <UserPlus size={15} /> Pipeline
            </button>
            <button className={`view-toggle-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
              <FileText size={15} /> All Estimates
            </button>
          </div>
          <button className="leads-followup-btn" onClick={openFollowUpModal}>
            <Send size={15} /> Follow Up Emails
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/admin/estimates/new')}>
            <Plus size={16} /> New Estimate
          </button>
        </div>
      </div>

      {/* ===== PIPELINE VIEW ===== */}
      {view === 'pipeline' && (
        <>
          <div className="leads-tabs">
            {[
              { key: 'new', label: 'New' },
              { key: 'contacted', label: 'Contacted' },
              { key: 'walkthrough_scheduled', label: 'Walkthrough' },
              { key: 'estimate_in_progress', label: 'Estimating' },
              { key: 'estimate_sent', label: 'Sent' },
              { key: 'follow_up', label: 'Follow Up' },
              { key: 'won', label: 'Won' },
              { key: 'lost', label: 'Lost' },
              { key: 'all', label: 'All' },
            ].map(tab => (
              <button key={tab.key}
                className={`leads-tab ${pipelineFilter === tab.key ? 'active' : ''} ${tab.key === 'new' && plCounts.new > 0 ? 'has-new' : ''}`}
                onClick={() => setPipelineFilter(tab.key)}
              >
                {tab.label}
                <span className="leads-tab-count">{plCounts[tab.key] || 0}</span>
              </button>
            ))}
            <div className="leads-search">
              <Search size={14} />
              <input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <div className="leads-list">
            {pipelineLeads.length === 0 && (
              <div className="leads-empty">No {pipelineFilter === 'all' ? '' : (leadStatuses[pipelineFilter]?.label.toLowerCase() + ' ')}leads found</div>
            )}
            {pipelineLeads.map(lead => {
              const st = leadStatuses[lead._ls] || leadStatuses.new;
              return (
                <div key={lead.id} className={`leads-row ${lead._ls === 'new' ? 'new-lead' : ''}`} onClick={() => setSelectedLead(lead)}>
                  <div className="leads-row-avatar" style={{ background: st.color }}>
                    {lead._name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="leads-row-info">
                    <div className="leads-row-name">
                      {lead._name}
                      {lead._src === 'website' && <span className="leads-web-tag">Web</span>}
                      {lead._src === 'website_intake' && <span className="leads-web-tag">Web</span>}
                      {lead._ls === 'new' && <span className="leads-new-tag">NEW</span>}
                    </div>
                    <div className="leads-row-detail">{lead._svc} · {lead._addr}</div>
                  </div>
                  <div className="leads-row-meta">
                    <div className="leads-row-amount">{lead._amt > 0 ? formatCurrency(lead._amt).replace('.00', '') : '—'}</div>
                    <div className="leads-row-date">{timeAgo(lead._at)}</div>
                  </div>
                  <span className="leads-row-status" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                  <ChevronRight size={16} className="leads-row-arrow" />
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ===== LIST VIEW ===== */}
      {view === 'list' && (
        <>
          <div className="estimate-stats">
            {[
              { key: 'pending_review', label: 'Needs Review', icon: AlertCircle },
              { key: 'draft', label: 'Drafts', icon: FileText },
              { key: 'sent', label: 'Awaiting Response', icon: Send },
              { key: 'approved', label: 'Ready to Schedule', icon: CheckCircle },
            ].map(s => (
              <div key={s.key} className="stat-card">
                <div className={`stat-icon ${s.key}`}><s.icon size={20} /></div>
                <div className="stat-info">
                  <span className="stat-value">{listCounts[s.key] || 0}</span>
                  <span className="stat-label">{s.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="filters-bar">
            <div className="search-box">
              <Search size={18} />
              <input placeholder="Search by client or address..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <div className="filter-tabs">
              {['all', 'pending_review', 'draft', 'sent', 'approved', 'declined'].map(status => (
                <button key={status}
                  className={`filter-tab ${listStatusFilter === status ? 'active' : ''}`}
                  onClick={() => setListStatusFilter(status)}
                >
                  {status === 'all' ? 'All' : (estimateStatuses[status]?.label || status)}
                  <span className="count">{status === 'all' ? listCounts.all : (listCounts[status] || 0)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="estimates-list">
            {filteredList.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <h3>No estimates found</h3>
                {!searchTerm && listStatusFilter === 'all' && (
                  <button className="btn btn-primary" onClick={() => navigate('/admin/estimates/new')}>
                    <Plus size={18} /> Create Estimate
                  </button>
                )}
              </div>
            ) : (
              filteredList.map(est => {
                const sc = estimateStatuses[est.status];
                const cond = homeConditions[est.calculatorInputs?.condition];
                const freq = cleaningFrequencies[est.calculatorInputs?.frequency];
                return (
                  <div key={est.id} className={`estimate-card ${est.requiresReview ? 'needs-review' : ''}`} onClick={() => goTo(est)}>
                    <div className="estimate-card-header">
                      <div className="client-info">
                        <div className="client-avatar">{est._client ? `${est._client.firstName?.[0] || ''}${est._client.lastName?.[0] || ''}` : '??'}</div>
                        <div className="client-details">
                          <h3>{est._name}</h3>
                          <p className="property-address">{est._addr}</p>
                        </div>
                      </div>
                      <div className="status-badge" style={{ color: sc?.color, background: sc?.bgColor }}>{sc?.label}</div>
                    </div>
                    <div className="estimate-card-body">
                      {est._custom ? (
                        <div className="estimate-details">
                          <div className="detail-item"><span className="detail-label">Type</span><span className="detail-value custom-badge">
                            {est.serviceType === 'commercial' ? '🏢 Commercial' : est.serviceType === 'hoarding' ? '📦 Hoarding' : est.serviceType === 'junk_removal' ? '🚛 Junk Removal' : '✨ Custom'}
                          </span></div>
                          <div className="detail-item"><span className="detail-label">Items</span><span className="detail-value">{est.lineItems?.length || 0} line items</span></div>
                          <div className="detail-item"><span className="detail-label">Total</span><span className="detail-value price">{formatCurrency(est.total || 0)}</span></div>
                        </div>
                      ) : (
                        <div className="estimate-details">
                          <div className="detail-item"><span className="detail-label">Service</span><span className="detail-value">{freq?.label || '—'}</span></div>
                          <div className="detail-item"><span className="detail-label">Condition</span><span className="detail-value">{cond?.icon} {cond?.shortLabel || '—'}</span></div>
                          <div className="detail-item"><span className="detail-label">Sqft</span><span className="detail-value">{est.calculatorInputs?.sqft?.toLocaleString() || '—'}</span></div>
                          <div className="detail-item"><span className="detail-label">Recurring</span><span className="detail-value price">{formatCurrency(est.recurringTotal)}</span></div>
                        </div>
                      )}
                      {est.requiresReview && est.status === 'draft' && (
                        <div className="review-banner"><AlertCircle size={16} /><span>Requires manager review</span></div>
                      )}
                    </div>
                    <div className="estimate-card-footer">
                      <div className="footer-meta">
                        <span className="meta-item"><Calendar size={14} /> Created {formatDate(est.createdAt)}</span>
                        {est.sentAt && <span className="meta-item"><Send size={14} /> Sent {formatDate(est.sentAt)}</span>}
                      </div>
                      <div className="footer-actions">
                        {['draft', 'pending_review', 'declined', 'expired'].includes(est.status) && (
                          <button className="btn-action danger" onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete this estimate for ${est._name}?`)) {
                              try { await deleteEstimate(est.id); } catch (err) { alert(err.message); }
                            }
                          }} title="Delete"><Trash2 size={16} /></button>
                        )}
                        {est.status === 'approved' && (
                          <button className="btn-action primary" onClick={(e) => { e.stopPropagation(); navigate(`/admin/estimates/${est.id}/schedule`); }}>
                            <Calendar size={16} /> Schedule
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* ===== LEAD SLIDE-OVER ===== */}
      {selectedLead && !showCloseLost && (
        <div className="lm-overlay" onClick={() => setSelectedLead(null)}>
          <div className="lead-modal" onClick={e => e.stopPropagation()}>
            <div className="lm-header">
              <div className="lm-avatar" style={{ background: leadStatuses[selectedLead._ls]?.color || '#2c5f4e' }}>
                {selectedLead._name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="lm-header-info">
                <h3>{selectedLead._name}</h3>
                <div className="lm-header-sub">{selectedLead._svc} · {selectedLead._addr}</div>
              </div>
              <button className="lm-close" onClick={() => setSelectedLead(null)}><X size={18} /></button>
            </div>
            <div className="lm-status-flow">
              {leadStatusFlow.map((step, i) => {
                const ci = leadStatusFlow.indexOf(selectedLead._ls);
                const cur = step === selectedLead._ls;
                const done = i < ci || selectedLead._ls === 'won';
                const lost = selectedLead._ls === 'lost';
                return (
                  <React.Fragment key={step}>
                    {i > 0 && <span className="flow-arrow">→</span>}
                    <button className={`flow-step ${cur ? 'current' : ''} ${done ? 'done' : ''}`}
                      onClick={() => { if (!cur && !lost) updateLeadStatus(selectedLead.id, step); }} disabled={lost}>
                      {done && !cur && <Check size={10} />}{leadStatuses[step]?.label}
                    </button>
                  </React.Fragment>
                );
              })}
              <span className="flow-arrow">|</span>
              <button className={`flow-step lost ${selectedLead._ls === 'lost' ? 'current' : ''}`}
                onClick={() => { if (selectedLead._ls !== 'lost') setShowCloseLost(true); }}>Lost</button>
            </div>
            <div className="lm-body">
              {selectedLead._ls === 'lost' && selectedLead.lostReason && (
                <div className="lm-section"><div className="lm-lost-banner"><AlertCircle size={14} />
                  <span><strong>Lost:</strong> {closeLostReasons.find(r => r.value === selectedLead.lostReason)?.label || selectedLead.lostReason}
                    {selectedLead.lostReasonNote ? ` — ${selectedLead.lostReasonNote}` : ''}</span>
                </div></div>
              )}
              <div className="lm-section">
                <div className="lm-section-label">Contact</div>
                <div className="lm-info-grid">
                  <div className="lm-field"><span className="lm-field-label">Phone</span><span className="lm-field-value">{selectedLead.clientPhone || selectedLead._client?.phone || '—'}</span></div>
                  <div className="lm-field"><span className="lm-field-label">Email</span><span className="lm-field-value">{selectedLead.clientEmail || selectedLead._client?.email || '—'}</span></div>
                  <div className="lm-field"><span className="lm-field-label">Source</span><span className="lm-field-value">{selectedLead._src === 'website' || selectedLead._src === 'website_intake' ? 'Website' : 'Manual'}</span></div>
                  <div className="lm-field"><span className="lm-field-label">Submitted</span><span className="lm-field-value">{selectedLead._at ? format(new Date(selectedLead._at), 'MMM d, yyyy · h:mm a') : '—'}</span></div>
                </div>
                <div className="lm-contact-bar">
                  {(selectedLead.clientPhone || selectedLead._client?.phone) && <a href={`tel:${selectedLead.clientPhone || selectedLead._client?.phone}`} className="lm-contact-pill"><Phone size={14} /> Call</a>}
                  {(selectedLead.clientPhone || selectedLead._client?.phone) && <a href={`sms:${selectedLead.clientPhone || selectedLead._client?.phone}`} className="lm-contact-pill"><MessageSquare size={14} /> Text</a>}
                  {(selectedLead.clientEmail || selectedLead._client?.email) && <a href={`mailto:${selectedLead.clientEmail || selectedLead._client?.email}`} className="lm-contact-pill"><Mail size={14} /> Email</a>}
                </div>
              </div>
              <div className="lm-section">
                <div className="lm-section-label">Property</div>
                <div className="lm-info-grid">
                  {selectedLead.calculatorInputs?.sqft > 0 && <div className="lm-field"><span className="lm-field-label">Size</span><span className="lm-field-value">{Number(selectedLead.calculatorInputs.sqft).toLocaleString()} sqft</span></div>}
                  {(selectedLead.calculatorInputs?.bedrooms > 0) && <div className="lm-field"><span className="lm-field-label">Beds/Baths</span><span className="lm-field-value">{selectedLead.calculatorInputs.bedrooms}bd / {selectedLead.calculatorInputs.fullBaths || selectedLead.calculatorInputs.bathrooms || 0}ba</span></div>}
                  {selectedLead.calculatorInputs?.condition && <div className="lm-field"><span className="lm-field-label">Condition</span><span className="lm-field-value">{homeConditions[selectedLead.calculatorInputs.condition]?.shortLabel || selectedLead.calculatorInputs.condition}</span></div>}
                </div>
              </div>
              {selectedLead.walkthroughDate && (
                <div className="lm-section">
                  <div className="lm-section-label">Walkthrough</div>
                  <div className="lm-info-grid">
                    <div className="lm-field"><span className="lm-field-label">Date</span><span className="lm-field-value">{formatDate(selectedLead.walkthroughDate, 'EEEE, MMM d, yyyy')}</span></div>
                    {selectedLead.walkthroughTime && <div className="lm-field"><span className="lm-field-label">Time</span><span className="lm-field-value">{selectedLead.walkthroughTime}</span></div>}
                    {selectedLead.walkthroughNotes && <div className="lm-field"><span className="lm-field-label">Notes</span><span className="lm-field-value">{selectedLead.walkthroughNotes}</span></div>}
                  </div>
                </div>
              )}
              <div className="lm-section">
                <div className="lm-section-label"><span>Estimate</span>
                  <Link to={`/admin/estimates/${selectedLead.id}`} className="lm-section-action" onClick={() => setSelectedLead(null)}>View Full <ExternalLink size={11} /></Link>
                </div>
                <div className="lm-estimate-card" onClick={() => { setSelectedLead(null); goTo(selectedLead); }}>
                  <div className="lm-est-info">
                    <span className="lm-est-label">{selectedLead.estimateNumber || `EST-${selectedLead.id?.slice(-6)}`}</span>
                    <span className="lm-est-amount">{selectedLead._amt > 0 ? formatCurrency(selectedLead._amt) : '—'}</span>
                  </div>
                  <span className="lm-est-status" style={{ background: leadStatuses[selectedLead._ls]?.bg, color: leadStatuses[selectedLead._ls]?.color }}>
                    {leadStatuses[selectedLead._ls]?.label}
                  </span>
                </div>
              </div>
              <div className="lm-section">
                <div className="lm-section-label">Activity & Notes</div>
                <div className="lm-timeline">
                  {(selectedLead._act || []).slice().reverse().map((item, i) => (
                    <div key={i} className="lm-tl-item">
                      <div className={`lm-tl-dot ${item.type}`}></div>
                      <div className="lm-tl-content">
                        <div className="lm-tl-text">{item.text}</div>
                        <div className="lm-tl-meta">{item.user || 'System'} · {item.timestamp ? format(new Date(item.timestamp), 'MMM d, h:mm a') : ''}</div>
                      </div>
                    </div>
                  ))}
                  {(!selectedLead._act || selectedLead._act.length === 0) && (
                    <div className="lm-tl-item"><div className="lm-tl-dot system"></div><div className="lm-tl-content">
                      <div className="lm-tl-text">Lead created</div>
                      <div className="lm-tl-meta">System · {selectedLead._at ? format(new Date(selectedLead._at), 'MMM d, h:mm a') : '—'}</div>
                    </div></div>
                  )}
                </div>
                <div className="lm-add-note">
                  <input className="lm-note-input" placeholder="Add a note..." value={newNote}
                    onChange={e => setNewNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newNote.trim()) addNote(); }} />
                  <button className="lm-note-btn" onClick={addNote} disabled={!newNote.trim()}>Add Note</button>
                </div>
              </div>
            </div>
            <div className="lm-footer">
              {selectedLead._ls !== 'won' && selectedLead._ls !== 'lost' && (
                <>
                  <button className="lm-btn primary" onClick={() => updateLeadStatus(selectedLead.id, 'won')}><Check size={15} /> Mark Won</button>
                  {selectedLead._ls === 'new' && <button className="lm-btn secondary" onClick={() => updateLeadStatus(selectedLead.id, 'contacted')}><Phone size={15} /> Contacted</button>}
                  {selectedLead._ls === 'contacted' && <button className="lm-btn secondary" onClick={() => updateLeadStatus(selectedLead.id, 'walkthrough_scheduled')}><MapPin size={15} /> Schedule Walkthrough</button>}
                  {selectedLead._ls === 'walkthrough_scheduled' && <button className="lm-btn secondary" onClick={() => updateLeadStatus(selectedLead.id, 'estimate_in_progress')}><FileText size={15} /> Start Estimate</button>}
                  {selectedLead._ls === 'estimate_in_progress' && <button className="lm-btn secondary" onClick={() => { setSelectedLead(null); goTo(selectedLead); }}><Send size={15} /> Review & Send</button>}
                  {selectedLead._ls === 'estimate_sent' && <button className="lm-btn secondary" onClick={() => updateLeadStatus(selectedLead.id, 'follow_up')}><RefreshCw size={15} /> Follow Up</button>}
                  {selectedLead._ls === 'follow_up' && <button className="lm-btn secondary" onClick={() => { setSelectedLead(null); goTo(selectedLead); }}><FileText size={15} /> View Estimate</button>}
                  <button className="lm-btn danger" onClick={() => setShowCloseLost(true)}><X size={15} /> Close Lost</button>
                </>
              )}
              {selectedLead._ls === 'won' && <button className="lm-btn primary" onClick={() => { setSelectedLead(null); navigate(`/admin/estimates/${selectedLead.id}/schedule`); }}><Calendar size={15} /> Schedule Job</button>}
              {selectedLead._ls === 'lost' && <button className="lm-btn secondary" onClick={() => updateLeadStatus(selectedLead.id, 'new')}><RefreshCw size={15} /> Reopen</button>}
            </div>
          </div>
        </div>
      )}

      {/* ===== WALKTHROUGH SCHEDULING MODAL ===== */}
      {showWalkthrough && selectedLead && (
        <div className="lm-overlay" onClick={() => setShowWalkthrough(false)}>
          <div className="close-lost-modal" onClick={e => e.stopPropagation()}>
            <div className="lm-header" style={{ background: '#ecfeff', borderBottomColor: '#a5f3fc' }}>
              <div className="lm-avatar" style={{ background: '#0891b2' }}><MapPin size={18} /></div>
              <div className="lm-header-info"><h3>Schedule Walkthrough</h3><div className="lm-header-sub">{selectedLead._name} · {selectedLead._addr}</div></div>
              <button className="lm-close" onClick={() => setShowWalkthrough(false)}><X size={18} /></button>
            </div>
            <div className="cl-body" style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="cl-label" style={{ marginBottom: 6, display: 'block' }}>Date *</label>
                  <input type="date" value={walkthroughData.date} onChange={e => setWalkthroughData(prev => ({ ...prev, date: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} />
                </div>
                <div>
                  <label className="cl-label" style={{ marginBottom: 6, display: 'block' }}>Time</label>
                  <input type="time" value={walkthroughData.time} onChange={e => setWalkthroughData(prev => ({ ...prev, time: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="cl-label" style={{ marginBottom: 6, display: 'block' }}>Duration (minutes)</label>
                <select value={walkthroughData.duration} onChange={e => setWalkthroughData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}>
                  <option value={15}>15 min</option><option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>1 hour</option>
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="cl-label" style={{ marginBottom: 6, display: 'block' }}>Notes</label>
                <textarea value={walkthroughData.notes} onChange={e => setWalkthroughData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Gate code, parking info, special instructions..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, minHeight: 70, resize: 'vertical' }} />
              </div>
              {(selectedLead.clientEmail || selectedLead._client?.email) && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#4a5568', cursor: 'pointer' }}>
                  <input type="checkbox" checked={walkthroughData.sendToClient} onChange={e => setWalkthroughData(prev => ({ ...prev, sendToClient: e.target.checked }))} />
                  Send confirmation to client ({selectedLead.clientEmail || selectedLead._client?.email})
                </label>
              )}
            </div>
            <div className="lm-footer">
              <button className="lm-btn secondary" onClick={() => setShowWalkthrough(false)}>Cancel</button>
              <button className="lm-btn primary" onClick={handleWalkthroughConfirm} disabled={!walkthroughData.date || walkthroughSending}>
                {walkthroughSending ? 'Scheduling...' : <><Calendar size={15} /> Schedule Walkthrough</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CLOSE LOST MODAL ===== */}
      {showCloseLost && selectedLead && (
        <div className="lm-overlay" onClick={() => setShowCloseLost(false)}>
          <div className="close-lost-modal" onClick={e => e.stopPropagation()}>
            <div className="lm-header" style={{ background: '#fef2f2', borderBottomColor: '#fecaca' }}>
              <div className="lm-avatar" style={{ background: '#dc2626' }}>✗</div>
              <div className="lm-header-info"><h3>Close Lost</h3><div className="lm-header-sub">{selectedLead._name}</div></div>
              <button className="lm-close" onClick={() => setShowCloseLost(false)}><X size={18} /></button>
            </div>
            <div className="cl-body">
              <label className="cl-label">Why was this lead lost?</label>
              <div className="cl-reasons">
                {closeLostReasons.map(r => (
                  <button key={r.value} className={`cl-reason ${lostReason === r.value ? 'active' : ''}`} onClick={() => setLostReason(r.value)}>{r.label}</button>
                ))}
              </div>
              {lostReason && <input className="cl-note-input" placeholder={lostReason === 'other' ? 'Please specify...' : 'Optional note...'} value={lostReasonNote} onChange={e => setLostReasonNote(e.target.value)} autoFocus={lostReason === 'other'} />}
            </div>
            <div className="lm-footer">
              <button className="lm-btn secondary" onClick={() => setShowCloseLost(false)}>Cancel</button>
              <button className="lm-btn danger" onClick={handleCloseLost} disabled={!lostReason}><X size={15} /> Confirm Close Lost</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== FOLLOW-UP EMAIL MODAL ===== */}
      {showFollowUpModal && (
        <div className="lm-overlay" onClick={() => { if (!followUpSending) setShowFollowUpModal(false); }}>
          <div className="fu-modal" onClick={e => e.stopPropagation()}>
            <div className="lm-header" style={{ background: '#eff6ff', borderBottomColor: '#bfdbfe' }}>
              <div className="lm-avatar" style={{ background: '#2563eb' }}><Send size={18} /></div>
              <div className="lm-header-info">
                <h3>Send Follow-Up Emails</h3>
                <div className="lm-header-sub">{followUpEligibleLeads.length} lead{followUpEligibleLeads.length !== 1 ? 's' : ''} with estimates sent · sorted oldest first</div>
              </div>
              <button className="lm-close" onClick={() => { if (!followUpSending) setShowFollowUpModal(false); }}><X size={18} /></button>
            </div>

            {followUpResults && (
              <div className={`fu-results-banner ${followUpResults.failed > 0 ? 'has-errors' : ''}`}>
                {followUpResults.sent > 0 && <span><Check size={14} /> {followUpResults.sent} email{followUpResults.sent !== 1 ? 's' : ''} sent successfully</span>}
                {followUpResults.failed > 0 && <span className="fu-results-error"><AlertTriangle size={14} /> {followUpResults.failed} failed: {followUpResults.errors.join('; ')}</span>}
              </div>
            )}

            <div className="fu-select-bar">
              <button className="fu-select-all-btn" onClick={followUpSelectAll}>
                {followUpEligibleLeads.length > 0 && followUpEligibleLeads.every(l => followUpSelected[l.id]) ? <CheckSquare size={16} /> : <Square size={16} />}
                Select All
              </button>
              <span className="fu-select-count">{followUpSelectedCount} selected</span>
            </div>

            <div className="fu-body">
              {followUpEligibleLeads.length === 0 && (
                <div className="leads-empty">No leads with sent estimates to follow up on</div>
              )}
              {followUpEligibleLeads.map(lead => {
                const isSelected = !!followUpSelected[lead.id];
                const st = leadStatuses[lead._ls] || leadStatuses.estimate_sent;
                return (
                  <div key={lead.id} className={`fu-row ${isSelected ? 'selected' : ''}`} onClick={() => followUpToggle(lead.id)}>
                    <div className="fu-row-check">
                      {isSelected ? <CheckSquare size={18} className="fu-checked" /> : <Square size={18} />}
                    </div>
                    <div className="fu-row-avatar" style={{ background: st.color }}>
                      {lead._name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="fu-row-info">
                      <div className="fu-row-name">
                        {lead._name}
                        <span className="fu-row-status-tag" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                      </div>
                      <div className="fu-row-detail">{lead._svc} · {lead._addr} · {lead._email}</div>
                    </div>
                    <div className="fu-row-meta">
                      <div className="fu-row-amount">{lead._amt > 0 ? formatCurrency(lead._amt).replace('.00', '') : '—'}</div>
                      <div className="fu-row-date">Sent {lead._dateSent ? format(new Date(lead._dateSent), 'MMM d') : '—'}</div>
                    </div>
                    <button className="fu-preview-btn" title="Preview email" onClick={(e) => { e.stopPropagation(); setFollowUpPreviewLead(lead); }}>
                      <Eye size={15} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="lm-footer">
              <button className="lm-btn secondary" onClick={() => setShowFollowUpModal(false)} disabled={followUpSending}>Cancel</button>
              <button className="lm-btn primary" onClick={handleBatchFollowUp} disabled={followUpSelectedCount === 0 || followUpSending}>
                {followUpSending ? 'Sending...' : <><Send size={15} /> Send {followUpSelectedCount > 0 ? `${followUpSelectedCount} ` : ''}Follow Up{followUpSelectedCount !== 1 ? 's' : ''}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EMAIL PREVIEW MODAL ===== */}
      {followUpPreviewLead && (
        <div className="lm-overlay" onClick={() => setFollowUpPreviewLead(null)} style={{ zIndex: 1010 }}>
          <div className="fu-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="lm-header">
              <div className="lm-avatar" style={{ background: '#2563eb' }}><Mail size={18} /></div>
              <div className="lm-header-info">
                <h3>Email Preview</h3>
                <div className="lm-header-sub">To: {followUpPreviewLead._email}</div>
              </div>
              <button className="lm-close" onClick={() => setFollowUpPreviewLead(null)}><X size={18} /></button>
            </div>
            <div className="fu-preview-body">
              <div className="fu-preview-subject">
                <span className="fu-preview-label">Subject:</span>
                Following Up on Your Cleaning Estimate - Two Sisters
              </div>
              <div className="fu-preview-content">
                <div className="fu-preview-email">
                  <h3 style={{ fontFamily: 'Georgia, serif', color: '#1a2634', marginTop: 0 }}>Just Checking In!</h3>
                  <p>Hi {followUpPreviewLead._name},</p>
                  <p>
                    We sent over your cleaning estimate{' '}
                    {followUpPreviewLead._dateSent ? `on ${format(new Date(followUpPreviewLead._dateSent), 'MMMM d')}` : 'recently'}{' '}
                    and wanted to follow up. We'd love to help get your home feeling fresh and taken care of!
                  </p>
                  {(followUpPreviewLead._addr || followUpPreviewLead._amt > 0) && (
                    <div className="fu-preview-info-box">
                      {followUpPreviewLead._addr && <div><span className="fu-preview-info-label">Property</span>{followUpPreviewLead._addr}</div>}
                      {followUpPreviewLead._svc && <div><span className="fu-preview-info-label">Service</span>{followUpPreviewLead._svc}</div>}
                      {followUpPreviewLead._amt > 0 && <div><span className="fu-preview-info-label">Estimate</span>{formatCurrency(followUpPreviewLead._amt)}</div>}
                    </div>
                  )}
                  <p>Ready to book? You can review your estimate and pick a date that works for you right from the link below:</p>
                  <div style={{ textAlign: 'center', margin: '16px 0' }}>
                    <span className="fu-preview-cta">View Estimate & Book Now</span>
                  </div>
                  <div className="fu-preview-talk-box">
                    <strong>Prefer to talk?</strong><br />
                    Call or text us at (434) 207-2246 or just reply to this email.
                  </div>
                  <p style={{ textAlign: 'center', color: '#6b7280' }}>We look forward to hearing from you!</p>
                </div>
              </div>
            </div>
            <div className="lm-footer">
              <button className="lm-btn secondary" onClick={() => setFollowUpPreviewLead(null)}>Close Preview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
